import type { ReactNode } from "react";
import type { PromoInsightsDTO } from "@easyshop/shared";
import { Card, ErrorState, Loading, cx } from "@/components/ui";
import { usePromoInsights } from "@/lib/queries";
import { dateFR, eur } from "@/lib/format";

const fmt = (n: number | null | undefined) => (n == null ? "—" : `${n >= 0 ? "+" : "−"}${Math.abs(Math.round(n))} %`);
const tone = (n: number | null | undefined) => (n == null ? "text-faint" : n >= 3 ? "text-positive" : n <= -3 ? "text-danger" : "text-muted");

const TONES = {
  positive: "bg-positive-bg text-[oklch(0.3_0.08_152)]",
  warn: "bg-warn-bg text-warn-ink",
  info: "bg-accent-soft text-[oklch(0.3_0.03_152)]",
} as const;

/**
 * Analyse des promos : effet de chaque campagne vs des jours ordinaires,
 * meilleurs jours, saisonnalité, rythme. Même composant en PC et en mobile
 * (`compact`), sur tout le périmètre ou une sélection de boutiques.
 */
export function PromoInsights({ boutiqueIds, compact = false }: { boutiqueIds?: string[]; compact?: boolean }) {
  const q = usePromoInsights(boutiqueIds);
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorState error={q.error} />;
  const d = q.data!;
  return (
    <div className="flex flex-col gap-4">
      <Method d={d} />
      <div className={cx("grid gap-3", compact ? "grid-cols-2" : "grid-cols-2 lg:grid-cols-4")}>
        <Kpi label="Effet moyen des promos" value={fmt(d.overallUpliftPct)} valueClass={tone(d.overallUpliftPct)} hint="CA des jours de promo vs jours ordinaires" />
        <Kpi label="Semaine d'après" value={fmt(d.afterEffectPct)} valueClass={tone(d.afterEffectPct)} hint="CA des 7 jours qui suivent" />
        <Kpi label="Jours en promo (90 j)" value={`${d.promoShare90} %`} valueClass={d.promoShare90 >= 35 ? "text-warn" : undefined} hint="Part des jours d'ouverture" />
        <Kpi label="Promos mesurées" value={String(d.campaigns.filter((c) => c.upliftPct != null).length)} hint={`sur ${d.campaigns.length} enregistrée${d.campaigns.length > 1 ? "s" : ""}`} />
      </div>

      {d.recommendations.length > 0 && (
        <Card pad={compact ? "p-4" : "px-6 py-5"}>
          <div className="mb-3 text-[14px] font-bold">Conseils</div>
          <div className="flex flex-col gap-2">
            {d.recommendations.map((r, i) => (
              <div key={i} className={cx("rounded-card-sm px-4 py-3 text-[13px] leading-[1.45]", TONES[r.tone])}>
                {r.text}
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className={cx("grid gap-4", !compact && "lg:grid-cols-2")}>
        <Card pad={compact ? "p-4" : "px-6 py-5"}>
          <Title sub="Activité habituelle et effet d'une promo, par jour de la semaine">Quel jour pour une promo ?</Title>
          <IndexTable
            rows={d.byWeekday.map((w) => ({ key: w.label, label: w.label, index: w.activityIndex, days: w.promoDays, uplift: w.upliftPct }))}
            indexLabel="Activité"
          />
        </Card>
        <Card pad={compact ? "p-4" : "px-6 py-5"}>
          <Title sub="Saisonnalité (hors promo) et effet des promos, par mois">Quand en faire, quand se calmer ?</Title>
          <IndexTable
            rows={d.byMonth.map((m) => ({ key: m.label, label: m.label, index: m.seasonIndex, days: m.promoDays, uplift: m.upliftPct }))}
            indexLabel="Saison"
          />
        </Card>
      </div>

      {d.byGap.length > 0 && (
        <Card pad={compact ? "p-4" : "px-6 py-5"}>
          <Title sub="Effet d'une promo selon le temps écoulé depuis la précédente, dans la même boutique">Rythme des promos</Title>
          <div className="flex flex-col gap-2">
            {d.byGap.map((g) => (
              <div key={g.label} className="grid grid-cols-[1fr_auto_70px] items-center gap-3 text-[12.5px]">
                <span className="font-semibold">{g.label}</span>
                <span className="text-[11px] text-muted-2">
                  {g.count} promo{g.count > 1 ? "s" : ""}-boutique
                </span>
                <span className={cx("text-right font-bold tabular", tone(g.upliftPct))}>{fmt(g.upliftPct)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card pad="p-0" className="overflow-hidden">
        <div className={cx("border-b border-line-soft", compact ? "px-4 py-3" : "px-6 py-4")}>
          <Title sub="CA réel vs CA attendu un jour ordinaire, sur les jours déjà passés">Résultat par campagne</Title>
        </div>
        {d.campaigns.length === 0 && <div className="px-6 py-5 text-[12.5px] text-muted">Aucune campagne enregistrée.</div>}
        {d.campaigns.map((c) => (
          <div key={c.id} className={cx("border-b border-line-row last:border-b-0", compact ? "px-4 py-3" : "px-6 py-[14px]")}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="min-w-0 flex-1 truncate text-[13px] font-bold">{c.name}</span>
              {c.origin === "BOUTIQUE" && (
                <span className="rounded-full bg-franchise-bg px-[8px] py-[2px] text-[10px] font-bold text-franchise">Boutique</span>
              )}
              <span className={cx("font-display text-[16px] font-extrabold tabular", tone(c.upliftPct))}>{fmt(c.upliftPct)}</span>
            </div>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[11.5px] text-muted-2">
              <span>
                {dateFR(c.startDate)} → {dateFR(c.endDate)} · {c.days} j · {c.boutiques} boutique{c.boutiques > 1 ? "s" : ""}
              </span>
              {c.measuredDays > 0 ? (
                <>
                  <span>
                    {eur(c.revenue)} vs {eur(c.baseline)} attendus
                  </span>
                  <span>Tickets {fmt(c.ticketsUpliftPct)}</span>
                  <span>Panier {fmt(c.basketUpliftPct)}</span>
                  <span>Semaine d'après {fmt(c.afterUpliftPct)}</span>
                </>
              ) : (
                <span>{c.status === "PLANNED" ? "À venir" : "Pas encore mesurable"}</span>
              )}
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}

function Method({ d }: { d: PromoInsightsDTO }) {
  return (
    <div className="text-[11.5px] leading-[1.5] text-muted-2">
      Chaque jour de promo est comparé au même jour de la semaine sur les 8 semaines précédentes, jours de promo exclus. Analyse sur{" "}
      {d.boutiques} boutique{d.boutiques > 1 ? "s" : ""}
      {d.since ? `, ventes depuis le ${dateFR(d.since)}` : ""}. Plus vous enregistrez de promos (réseau et boutiques), plus les conseils sont fiables.
    </div>
  );
}

function Title({ children, sub }: { children: ReactNode; sub: string }) {
  return (
    <div className="mb-3">
      <div className="text-[14px] font-bold">{children}</div>
      <div className="mt-[2px] text-[11.5px] text-muted-2">{sub}</div>
    </div>
  );
}

function Kpi({ label, value, hint, valueClass }: { label: string; value: string; hint: string; valueClass?: string }) {
  return (
    <Card pad="px-4 py-[14px]" className="min-w-0">
      <div className="truncate text-[11.5px] font-semibold text-muted">{label}</div>
      <div className={cx("mt-1 font-display text-[22px] font-extrabold tabular", valueClass)}>{value}</div>
      <div className="mt-[2px] truncate text-[10.5px] text-faint">{hint}</div>
    </Card>
  );
}

/** Lignes « libellé · indice (barre autour de 100) · jours de promo · effet ». */
function IndexTable({ rows, indexLabel }: { rows: { key: string; label: string; index: number | null; days: number; uplift: number | null }[]; indexLabel: string }) {
  const max = Math.max(130, ...rows.map((r) => r.index ?? 0));
  return (
    <div>
      <div className="grid grid-cols-[82px_1fr_52px_58px] gap-2 pb-2 text-[10px] font-bold uppercase text-muted-2">
        <span />
        <span>{indexLabel} (100 = moyenne)</span>
        <span className="text-right">Promos</span>
        <span className="text-right">Effet</span>
      </div>
      {rows.map((r) => (
        <div key={r.key} className="grid grid-cols-[82px_1fr_52px_58px] items-center gap-2 border-t border-line-soft py-[7px] text-[12px]">
          <span className="truncate font-semibold">{r.label}</span>
          <span className="flex items-center gap-2">
            <span className="relative h-[6px] flex-1 overflow-hidden rounded-[3px] bg-track">
              {r.index != null && (
                <span
                  className={cx("absolute inset-y-0 left-0", r.index >= 110 ? "bg-positive" : r.index <= 90 ? "bg-warn" : "bg-product-bar")}
                  style={{ width: `${Math.min(100, (r.index / max) * 100)}%` }}
                />
              )}
              <span className="absolute inset-y-[-2px] w-px bg-muted-2" style={{ left: `${(100 / max) * 100}%` }} />
            </span>
            <span className="w-[30px] text-right tabular text-muted">{r.index ?? "—"}</span>
          </span>
          <span className="text-right tabular text-muted-2">{r.days || "—"}</span>
          <span className={cx("text-right font-bold tabular", tone(r.uplift))}>{fmt(r.uplift)}</span>
        </div>
      ))}
    </div>
  );
}
